/**
 * Друковані довідки працівника (рішення власника 2026-08-19):
 * ?form=work — довідка з місця роботи; ?form=income — довідка про доходи
 * (помісячно з проведених відомостей). Кнопки — уніфікований PrintActions
 * («друкувати» / «назад» / «підписати і зберегти»): знімок форми з
 * печаткою/підписом зберігається у вкладення картки працівника.
 */
import { useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import { employees } from '@/api/hr';
import { orgs } from '@/api/catalogs';
import PrintActions from '@/shared/components/PrintActions';
import { partyCodesText } from '@/shared/utils/partyCodes';

const MONTHS = ['', 'січня', 'лютого', 'березня', 'квітня', 'травня',
  'червня', 'липня', 'серпня', 'вересня', 'жовтня', 'листопада', 'грудня'];
const MONTH_NAMES = ['', 'Січень', 'Лютий', 'Березень', 'Квітень', 'Травень',
  'Червень', 'Липень', 'Серпень', 'Вересень', 'Жовтень', 'Листопад', 'Грудень'];

const fmt = (v: string | number) => Number(v).toLocaleString('uk-UA',
  { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function todayUa(): string {
  const d = new Date();
  return `«${d.getDate()}» ${MONTHS[d.getMonth() + 1]} ${d.getFullYear()} р.`;
}

export default function EmployeeCertificatePrintPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const form = params.get('form') === 'income' ? 'income' : 'work';

  const empQ = useQuery({
    queryKey: ['employee', id],
    queryFn: () => employees.get(id!),
    enabled: !!id,
  });
  const orgsQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const incomeQ = useQuery({
    queryKey: ['employee-income', id],
    queryFn: () => api.get<{ months: {
      year: number; month: number; gross: string; withheld: string;
      net: string }[] }>(`/employees/${id}/income-summary`, {
      params: { months: 6 } }).then((r) => r.data),
    enabled: !!id && form === 'income',
  });

  useEffect(() => {
    document.body.classList.add('print-mode');
    return () => document.body.classList.remove('print-mode');
  }, []);

  const emp = empQ.data;
  const org = orgsQ.data?.items.find((o) => o.id === emp?.organization_id);
  if (!emp || !org) {
    return <div style={{ padding: '2rem' }}>Завантаження…</div>;
  }
  const title = form === 'income' ? 'Довідка про доходи' : 'Довідка з місця роботи';
  const rows = incomeQ.data?.months ?? [];
  const tGross = rows.reduce((t, r) => t + Number(r.gross), 0);
  const tWith = rows.reduce((t, r) => t + Number(r.withheld), 0);
  const tNet = rows.reduce((t, r) => t + Number(r.net), 0);

  return (
    <div className="print-page" style={{
      padding: '20mm', fontFamily: 'Times, serif', fontSize: '12pt',
      lineHeight: 1.5, maxWidth: '210mm', margin: '0 auto', background: 'var(--bg-panel)',
    }}>
      <PrintActions docMeta={{
        organizationId: emp.organization_id,
        documentType: 'ref.employees',
        documentId: emp.id,
        filename: `${title} — ${emp.full_name ?? ''}`,
        orgName: org.name,
      }} />

      <div style={{ marginBottom: '1rem' }}>
        <div style={{ fontWeight: 700 }}>{org.full_name || org.name}</div>
        {partyCodesText(org) && <div style={{ fontSize: '10pt' }}>{partyCodesText(org)}</div>}
        {org.legal_address && (
          <div style={{ fontSize: '10pt' }}>{org.legal_address}</div>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between',
        fontSize: '10.5pt', marginBottom: '1.5rem' }}>
        <span>№ ______</span>
        <span>{todayUa()}</span>
      </div>

      <h2 style={{ textAlign: 'center', fontSize: '14pt', margin: '0 0 1.2rem' }}>
        ДОВІДКА
      </h2>

      {form === 'work' ? (
        <p style={{ textIndent: '2em', textAlign: 'justify' }}>
          Видана про те, що <strong>{emp.full_name ?? '—'}</strong>
          {emp.inn ? ` (РНОКПП ${emp.inn})` : ''} дійсно працює
          у {org.full_name || org.name}
          {emp.position_name ? <> на посаді <strong>{emp.position_name}</strong></> : null}
          {emp.hire_date
            ? ` з ${new Date(emp.hire_date).toLocaleDateString('uk-UA')}`
            : ''} по теперішній час.
        </p>
      ) : (
        <>
          <p style={{ textIndent: '2em', textAlign: 'justify' }}>
            Видана про те, що <strong>{emp.full_name ?? '—'}</strong>
            {emp.inn ? ` (РНОКПП ${emp.inn})` : ''} дійсно працює
            у {org.full_name || org.name}
            {emp.position_name ? <> на посаді <strong>{emp.position_name}</strong></> : null}
            {emp.hire_date
              ? ` з ${new Date(emp.hire_date).toLocaleDateString('uk-UA')}`
              : ''} і {rows.length
              ? `за останні ${rows.length} міс. отримував(-ла) такі доходи:`
              : 'нарахованих доходів за проведеними відомостями не має.'}
          </p>
          {rows.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse',
              fontSize: '11pt', margin: '0.75rem 0' }}>
              <thead>
                <tr>
                  {['Місяць', 'Нараховано, грн', 'Утримано (ПДФО+ВЗ), грн',
                    'До виплати, грн'].map((h) => (
                    <th key={h} style={{ border: '1px solid #000',
                      padding: '4px 8px' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={`${r.year}-${r.month}`}>
                    <td style={{ border: '1px solid #000', padding: '4px 8px' }}>
                      {MONTH_NAMES[r.month]} {r.year}
                    </td>
                    <td style={{ border: '1px solid #000', padding: '4px 8px',
                      textAlign: 'right' }}>{fmt(r.gross)}</td>
                    <td style={{ border: '1px solid #000', padding: '4px 8px',
                      textAlign: 'right' }}>{fmt(r.withheld)}</td>
                    <td style={{ border: '1px solid #000', padding: '4px 8px',
                      textAlign: 'right' }}>{fmt(r.net)}</td>
                  </tr>
                ))}
                <tr style={{ fontWeight: 700 }}>
                  <td style={{ border: '1px solid #000', padding: '4px 8px' }}>
                    Разом
                  </td>
                  <td style={{ border: '1px solid #000', padding: '4px 8px',
                    textAlign: 'right' }}>{fmt(tGross)}</td>
                  <td style={{ border: '1px solid #000', padding: '4px 8px',
                    textAlign: 'right' }}>{fmt(tWith)}</td>
                  <td style={{ border: '1px solid #000', padding: '4px 8px',
                    textAlign: 'right' }}>{fmt(tNet)}</td>
                </tr>
              </tbody>
            </table>
          )}
        </>
      )}

      <p style={{ textIndent: '2em', marginTop: '1rem' }}>
        Довідка видана для подання за місцем вимоги.
      </p>

      <table style={{ width: '100%', marginTop: '3rem' }}>
        <tbody>
          <tr>
            <td style={{ width: '50%' }}>
              <div>Керівник</div>
              <div style={{ marginTop: '2rem', borderTop: '1px solid #000',
                width: '80%', textAlign: 'center', fontSize: '9pt',
                color: '#555' }}>(підпис, П.І.Б.)</div>
            </td>
            <td style={{ width: '50%' }}>
              <div>Головний бухгалтер</div>
              <div style={{ marginTop: '2rem', borderTop: '1px solid #000',
                width: '80%', textAlign: 'center', fontSize: '9pt',
                color: '#555' }}>(підпис, П.І.Б.)</div>
            </td>
          </tr>
        </tbody>
      </table>
      <p style={{ marginTop: '1rem' }}>М.П.</p>
    </div>
  );
}
